import React, { useState } from 'react';
import { useCategories, useBranches } from '../../db/hooks/useLookupData';
import { useDatabase } from '../../db/DatabaseProvider';
import { generateId, now } from '../../lib/uuid';
import { useHasPermission } from '../auth/RequirePermission';
import { useToast } from '../common/ToastProvider';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { Pencil, Trash2, Plus, Check, Pipette } from 'lucide-react';
import { CustomSelect } from '../common/CustomSelect';
import { Button } from '../common/Button';

export function CategoryManager() {
  const { categories, isLoading } = useCategories();
  const { branches } = useBranches();
  const db = useDatabase();
  const hasPermission = useHasPermission();
  const canManageConfig = hasPermission('settings:manage');
  const toast = useToast();

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => Promise<void>;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: async () => {},
  });

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [type, setType] = useState<'income' | 'expense' | 'both'>('expense');
  const [color, setColor] = useState('#94A3B8');
  const [icon, setIcon] = useState('📁');
  const [isActive, setIsActive] = useState(true);
  const [branchId, setBranchId] = useState('');

  const openNew = () => {
    setEditingId(null);
    setName('');
    setType('expense');
    setColor('#94A3B8');
    setIcon('📁');
    setIsActive(true);
    setBranchId('');
    setIsFormOpen(true);
  };

  const openEdit = (category: any) => {
    setEditingId(category.id);
    setName(category.name);
    setType(category.type);
    setColor(category.color || '#94A3B8');
    setIcon(category.icon || '📁');
    setIsActive(category.is_active !== false);
    setBranchId(category.branch_id || '');
    setIsFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!db || !canManageConfig) return;

    // Anti-orphan validations using $elemMatch
    const transactionCount = await db.transactions.count({
      selector: {
        items: { $elemMatch: { category_id: id } },
        is_deleted: { $ne: true }
      }
    }).exec();

    if (transactionCount > 0) {
      toast.warning(`Esta categoría contiene ${transactionCount} transactions asociadas y no puede ser eliminada. Por favor, desactívela en su lugar.`);
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: 'Eliminar categoría',
      message: '¿Estás seguro de que deseas eliminar esta categoría? Esta acción no se puede deshacer.',
      onConfirm: async () => {
        try {
          const doc = await db.categories.findOne(id).exec();
          if (doc) {
            await doc.patch({
              is_deleted: true,
              updated_at: now(),
              
            });
            toast.success('Categoría eliminada correctamente');
          }
        } catch (err: any) {
          toast.error('Error al eliminar categoría: ' + err.message);
        } finally {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (editingId) {
      const doc = await db.categories.findOne(editingId).exec();
      if (doc) {
        await doc.patch({
          name, type, color, icon,
          is_active: isActive,
          branch_id: branchId || null,
          updated_at: now(),
        });
      }
    } else {
      await db.categories.insert({
        id: generateId(),
        name, type, color, icon,
        is_active: isActive,
        branch_id: branchId || null,
        updated_at: now(),
        is_deleted: false,
        last_modified_by: 'system',
      });
    }
    setIsFormOpen(false);
  };

  if (isLoading) return <div className="p-6 text-center text-xs text-text-tertiary">Cargando categorías...</div>;

  return (
    <div className="border border-border bg-surface rounded-2xl shadow-xs overflow-hidden">
      {/* Harmonized Header Bar */}
      <div className="flex justify-between items-center px-6 py-4 border-b border-border bg-surface-secondary/40">
        <div>
          <h3 className="text-sm font-bold text-text-primary">Categorías</h3>
          <p className="text-xs text-text-secondary mt-0.5">Clasificación de ingresos y egresos</p>
        </div>
        {canManageConfig && (
          <Button
            variant="primary"
            size="sm"
            icon={Plus}
            onClick={() => {
              if (isFormOpen) {
                setIsFormOpen(false);
              } else {
                openNew();
              }
            }}
          >
            {isFormOpen ? 'Cerrar Formulario' : 'Nueva Categoría'}
          </Button>
        )}
      </div>

      {/* Expandable Creation Form */}
      {isFormOpen && (
        <form onSubmit={handleSubmit} className="p-5 border-b border-border bg-surface-secondary/50 space-y-4 animate-fade-in">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1">Nombre *</label>
              <input
                required
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full h-10 rounded-xl border border-border bg-surface px-3.5 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky transition-all shadow-xs"
                placeholder="Ej. Ventas de Productos"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1">Tipo de Flujo *</label>
              <CustomSelect
                value={type}
                onChange={(val) => setType(val as any)}
                options={[
                  { value: 'income', label: 'Ingreso (+)' },
                  { value: 'expense', label: 'Egreso (-)' },
                  { value: 'both', label: 'Ambos (Mixto)' },
                ]}
                buttonClassName="!py-2"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1">Icono / Emoji *</label>
              <input
                required
                type="text"
                value={icon}
                onChange={e => setIcon(e.target.value)}
                className="w-full h-10 rounded-xl border border-border bg-surface px-3.5 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky transition-all shadow-xs"
                placeholder="Ej. 💰"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1.5">Color Distintivo *</label>
              <div className="flex items-center gap-2 flex-wrap pt-0.5">
                {['#F97316', '#06B6D4', '#8B5CF6', '#10B981', '#EC4899', '#EAB308', '#3B82F6'].map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={`w-7 h-7 rounded-full transition-all cursor-pointer flex items-center justify-center border-2 ${
                      color === c ? 'border-white scale-110 shadow-sm' : 'border-transparent opacity-80 hover:opacity-100'
                    }`}
                    style={{ backgroundColor: c }}
                  >
                    {color === c && <Check className="w-3.5 h-3.5 text-white drop-shadow-sm" />}
                  </button>
                ))}

                <label
                  className={`relative w-7 h-7 rounded-full transition-all cursor-pointer flex items-center justify-center border-2 overflow-hidden ${
                    !['#F97316', '#06B6D4', '#8B5CF6', '#10B981', '#EC4899', '#EAB308', '#3B82F6'].includes(color)
                      ? 'border-white scale-110 shadow-md ring-2 ring-brand-sky/50'
                      : 'border-border/60 opacity-80 hover:opacity-100'
                  }`}
                  style={{
                    background: !['#F97316', '#06B6D4', '#8B5CF6', '#10B981', '#EC4899', '#EAB308', '#3B82F6'].includes(color)
                      ? color
                      : 'conic-gradient(from 180deg at 50% 50%, #f43f5e 0deg, #eab308 60deg, #10b981 120deg, #06b6d4 180deg, #8b5cf6 240deg, #ec4899 300deg, #f43f5e 360deg)',
                  }}
                  title="Elegir color personalizado"
                >
                  <input
                    type="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                  />
                  <Pipette className="w-3.5 h-3.5 text-white drop-shadow-md pointer-events-none" />
                </label>
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-text-secondary mb-1">Sede de Visibilidad</label>
              <CustomSelect
                value={branchId}
                onChange={(val) => setBranchId(val)}
                options={[
                  { value: '', label: 'Todas las sedes' },
                  ...branches.map((s) => ({ value: s.id, label: s.name })),
                ]}
                buttonClassName="!py-2"
              />
            </div>
            <div className="sm:col-span-2 flex items-center gap-2 pt-1">
              <input
                type="checkbox"
                id="cat_activa"
                checked={isActive}
                onChange={e => setIsActive(e.target.checked)}
                className="h-4 w-4 rounded border-border text-brand-orange focus:ring-brand-orange bg-surface cursor-pointer"
              />
              <label htmlFor="cat_activa" className="text-xs font-bold text-text-primary cursor-pointer select-none">
                Categoría Habilitada para Operaciones
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
            <Button variant="ghost" size="sm" type="button" onClick={() => setIsFormOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" size="sm" type="submit">
              {editingId ? 'Actualizar Categoría' : 'Guardar Categoría'}
            </Button>
          </div>
        </form>
      )}

      {/* Table List */}
      <div>
        <table className="w-full text-left text-sm border-collapse">
          <thead className="bg-surface-secondary/60 text-xs uppercase text-text-tertiary border-b border-border">
            <tr>
              <th className="px-6 py-3.5 font-semibold">Icono</th>
              <th className="px-6 py-3.5 font-semibold">Nombre</th>
              <th className="px-6 py-3.5 font-semibold">Tipo</th>
              <th className="px-6 py-3.5 font-semibold">Visibilidad</th>
              <th className="px-6 py-3.5 font-semibold">Estado</th>
              <th className="px-6 py-3.5 font-semibold text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {categories.map(cat => {
              const branch = branches.find(s => s.id === cat.branch_id);
              return (
                <tr key={cat.id} className="hover:bg-surface-hover/30 transition-colors">
                  <td className="px-6 py-3.5">
                    <span style={{ backgroundColor: `${cat.color}15`, color: cat.color }} className="w-8 h-8 rounded-xl flex items-center justify-center text-base">
                      {cat.icon}
                    </span>
                  </td>
                  <td className="px-6 py-3.5 font-bold text-text-primary">{cat.name}</td>
                  <td className="px-6 py-3.5 text-text-secondary text-xs">{cat.type === 'income' ? 'Ingreso' : cat.type === 'expense' ? 'Egreso' : 'Ambos'}</td>
                  <td className="px-6 py-3.5 text-text-secondary text-xs">{branch ? branch.name : 'Global'}</td>
                  <td className="px-6 py-3.5">
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${cat.is_active !== false ? 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20' : 'bg-slate-500/10 text-slate-400 border border-slate-500/20'}`}>
                      {cat.is_active !== false ? 'Activa' : 'Inactiva'}
                    </span>
                  </td>
                  <td className="px-6 py-3.5 text-right">
                    {canManageConfig && (
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="xs" icon={Pencil} onClick={() => openEdit(cat)} aria-label="Editar" />
                        <Button variant="danger" size="xs" icon={Trash2} onClick={() => handleDelete(cat.id)} aria-label="Eliminar" />
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {categories.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-text-tertiary text-xs">No hay categorías registradas</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant="danger"
        confirmText="Eliminar"
      />
    </div>
  );
}
